import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "flowbite-react";
import { HiOutlineArrowLeft, HiOutlineUser } from "react-icons/hi";
import { Button } from "@components";
import { cn, panel, Text } from "@design-system";
import { useOrderTemplates } from "@data";
import { OrderTemplate } from "../../../models";
import { useOnboardingStatus } from "../../../hooks";
import CreateOrder from "./CreateOrder";

/**
 * Route wrapper for `/orders/new`.
 *
 * Guards the "create order" flow: a driver must already exist because it cannot
 * be created inside the wizard (unlike a customer, which the customer step lets
 * you add inline). So when there is no driver we show a friendly, actionable
 * screen instead of dropping the user into a wizard they cannot complete. This
 * is the authoritative gate: it covers every entry point into /orders/new.
 *
 * When a `templateId` is provided (via router state from the Templates page, or
 * a `?templateId=` query param), it resolves the template and prefills the
 * wizard with a fresh draft order built from it.
 */
const CreateOrderRoute: FC = () => {
  const { t } = useTranslation(["onboarding", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const status = useOnboardingStatus();

  const templateId =
    (location.state as { templateId?: string } | null)?.templateId ??
    searchParams.get("templateId") ??
    undefined;

  const templatesQuery = useOrderTemplates();

  const template = useMemo(() => {
    if (!templateId) return undefined;
    return (templatesQuery.data ?? []).find(
      (tpl: OrderTemplate) => tpl.id === templateId,
    );
  }, [templateId, templatesQuery.data]);

  // While the data resolves we render the wizard optimistically; the internal
  // steps validate anyway, and this avoids a flash of the blocked screen for a
  // workspace that actually has the prerequisites.
  if (status.isResolving || status.canCreateOrder) {
    if (templateId && templatesQuery.isLoading) {
      return (
        <div className="flex items-center justify-center h-full py-10">
          <Spinner />
        </div>
      );
    }

    if (template) {
      return <CreateOrder initialOrder={OrderTemplate.toOrder(template)} />;
    }

    return <CreateOrder />;
  }

  return (
    <div className="mx-auto w-full max-w-xl py-8">
      <div className={cn(panel, "p-6 sm:p-8")}>
        <Text as="h1" variant="section-title-lg">
          {t("onboarding:order_blocked.title")}
        </Text>
        <Text variant="secondary" className="mt-2">
          {t("onboarding:order_blocked.description")}
        </Text>

        <div className="mt-6 flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-lgb-primary/10 text-lgb-primary">
              <HiOutlineUser className="h-5 w-5" />
            </span>
            <Text variant="body" className="font-medium">
              {t("onboarding:order_blocked.driver")}
            </Text>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate("/resources/drivers?new=true")}
          >
            {t("onboarding:order_blocked.add_driver")}
          </Button>
        </div>

        <div className="mt-6">
          <Button
            variant="secondary"
            icon={<HiOutlineArrowLeft className="h-4 w-4" />}
            onClick={() => navigate("/")}
          >
            {t("onboarding:order_blocked.back")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderRoute;
